import { cn, getCurrencyString, getCurrencyValue } from '@theprintbar/shared';
import { Accordion } from '../Accordion';

interface PriceBreakdown {
	total?: string | number | null;
	subtotal?: string | number | null;
	quantity?: number | null;
	breakdown?: Array<{
		title?: string | null;
		amount?: string | number | null;
		singleAmount?: string | number | null;
	}> | null;
}

interface PriceBreakdownProps {
	pricing?: PriceBreakdown;
	className?: string;
	disabled?: boolean;
}

export function PriceBreakdown({ pricing, className, disabled }: PriceBreakdownProps) {
	if (!pricing) return null;

	const costItems = pricing?.breakdown?.filter(({ amount }) => !!amount && getCurrencyValue(amount) > 0) || [];
	const discounts = pricing?.breakdown?.filter(({ amount }) => !!amount && getCurrencyValue(amount) < 0) || [];
	const quantity = pricing?.quantity || 1;
	return (
		<div className={cn('mt-8', className)}>
			<Accordion
				disabled={disabled || !pricing.total || !costItems.length}
				title={<h5 className="text-xs uppercase">Price breakdown</h5>}
			>
				<div className="space-y-2.5">
					<div className="grid grid-cols-5 items-center justify-between">
						<p className="text-sm col-span-3"></p>
						<p className="text-sm text-right font-bold">{quantity > 1 && 'Per item'}</p>
						<p className="text-sm text-right font-bold">Total</p>
					</div>
					{costItems.map(({ title, singleAmount, amount }) => (
						<div key={title} className="grid grid-cols-5 items-center justify-between">
							<p className="text-sm col-span-3">{title}</p>
							<p className="text-sm text-right">{quantity > 1 && getCurrencyString(singleAmount)}</p>
							<p className="text-sm text-right">{getCurrencyString(amount)}</p>
						</div>
					))}
					{!!discounts.length && (
						<>
							<h5 className="text-xs uppercase">Discounts</h5>
							{discounts.map(({ title, singleAmount, amount }) => (
								<div key={title} className="grid grid-cols-5 items-center justify-between">
									<p className="text-sm col-span-3">{title}</p>
									<p className="text-sm text-right">{quantity > 1 && getCurrencyString(singleAmount)}</p>
									<p className="text-sm text-right">{getCurrencyString(amount)}</p>
								</div>
							))}
						</>
					)}
					<div className="grid grid-cols-5 items-center justify-between">
						<p className="text-sm col-span-3"></p>
						<p className="text-sm text-right border-t border-blue pt-2 h-5">
							{quantity > 1 &&
								getCurrencyString(
									pricing?.breakdown?.reduce((acc, { singleAmount }) => acc + getCurrencyValue(singleAmount), 0),
								)}
						</p>
						<p className="text-sm text-right border-t border-blue pt-2 h-5">{getCurrencyString(pricing.total)}</p>
					</div>
				</div>
			</Accordion>
		</div>
	);
}
